import { useQuery } from "@tanstack/react-query";
import { apiGet } from "./client";
import { useTicketSize } from "../ticket-size";

/** A card's change line: the figure, which way it moved, what it is measured against, and the figure it is measured against. */
export interface Change {
  value: string;
  direction: "up" | "down" | "flat";
}

/** The industry's (ELFA's) figure for the same thing as a Navitas card, shown on the right of that card in the same shape. */
export interface ElfaFigure {
  /** names the figure and its month, e.g. "ELFA (Sep'26)" */
  label: string;
  value: string;
  trend: Change;
  comparison?: string | null;
  secondValue?: string | null;
}

export interface GrowthStat {
  label: string;
  value: string;
  trend: Change;
  /** the text shown next to the change, e.g. "vs Aug'26" */
  comparison?: string | null;
  /** the figure the change is measured against, formatted like `value`, e.g. "$935.2M" */
  secondValue?: string | null;
  /** ELFA's figure for the same thing, on the right of the card; null/absent for a card ELFA has no counterpart of */
  elfa?: ElfaFigure | null;
}

/** One metric of the industry's monthly CFI release, month over month. Every field is display text, as published. */
export interface ElfaComparisonRow {
  category: string;
  metric: string;
  segment: string;
  asOf: string;
  comparedWith: string;
  value: string;
  previousValue: string;
  change: string;
  notes: string;
}

/** One month of the Our Growth Story comparison: the change in new business on the month before, in %. Null where it cannot be worked out. */
export interface MomTrendPoint {
  month: string;
  navitas: number | null;
  elfa: number | null;
}

export interface IndustryBenchmarkSummary {
  growthStats: GrowthStat[];
  newBusinessTrend: Array<{ month: string; volume: number }>;
  /** Navitas's and ELFA's month-over-month change in new business, for the same months as newBusinessTrend */
  momTrend: MomTrendPoint[];
  elfaComparison: ElfaComparisonRow[];
  /** false when the ELFA tables are not in this environment (sql/15_elfa_monthly.sql) */
  elfaAvailable: boolean;
}

export function useIndustryBenchmark() {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: ["industry-benchmark", ticket ?? "all"],
    queryFn: () => apiGet<IndustryBenchmarkSummary>("/industry-benchmark", { ticket }),
  });
}
